import {
  BackComponent,
  FormCheckBox,
  FormInput,
  FormPhone,
  FormSelect,
  Option,
  PageMainContent,
  Typography,
} from '@/components'
import { FormProvider, useFormContext, useSnackbar } from '@/providers'
import { useCompanyPoaStatus, useCreateCompanyUser } from '@/services'
import { useGoBack } from '@/utils'
import { map, pick, pipe, prop } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useOutletContext, useParams } from 'react-router-dom'
import { PageActions, PageTitle } from './styles'
import { validationSchema } from './validationSchema'
import { Button } from '@surein/ui'

interface Messages {
  general: Record<string, string>
  locationTypes: Record<string, string>
  selectCompany: string
  addUser: string
  back: string
  snackBars: Record<string, string>
  form: Record<string, string>
  newCompany: Record<string, string>
  users: {
    name: { label: string; placeholder: string }
    surname: { label: string; placeholder: string }
    email: { label: string; placeholder: string }
    phone: { label: string }
    isMD: { label: string }
  }
  validations: Record<string, string>
  pageTitle: string
  advisorText: string
}

interface FormValues {
  companyId?: string
  name: string
  surname: string
  email: string
  phone: string
  isMd: boolean
}

interface OutletContext {
  user: {
    companies: Array<{
      companyId: string
      name: string
    }>
  }
}

const useMessages = (): Messages => {
  const { t } = useTranslation()

  return {
    general: t('common', { returnObjects: true }),
    locationTypes: t(`locationPage.locationTypes`, { returnObjects: true }),
    selectCompany: t('companyPage.selectCompany'),
    addUser: t('companyPage.addCompanyUsers.addUser'),
    back: t('common.back'),
    snackBars: t('snackbars', { returnObjects: true }),
    form: t('profile.forms', { returnObjects: true }),
    newCompany: t('profile.newCompany', { returnObjects: true }),
    users: t('profile.users', { returnObjects: true }),
    validations: t('companyPage.addCompanyUsers.validations', {
      returnObjects: true,
    }),
    pageTitle: t('companyPage.addCompanyUsers.addUser'),
    advisorText: t('advisor_text_base'),
  }
}

const RenderIsMDField = () => {
  const { companyId } = useParams()
  const { values } = useFormContext<FormValues>()
  const messages = useMessages()

  const id = companyId || values.companyId
  const { data: POAstatus, isLoading } = useCompanyPoaStatus(id || '', {
    enabled: Boolean(id),
  })

  if (!id) return null

  return (
    !isLoading &&
    !POAstatus?.poaSigned && (
      <FormCheckBox
        disabled={POAstatus?.poaSigned}
        initialValue={false}
        label={messages.users.isMD.label}
        name="isMd"
      />
    )
  )
}

const makeCompanies = pipe(
  prop('companies'),
  map(pick(['companyId', 'name'])),
) as (user: OutletContext['user']) => Array<{ companyId: string; name: string }>

const PageForm = () => {
  const { t } = useTranslation()
  const { user } = useOutletContext<OutletContext>()
  const { companyId } = useParams()
  const snackbar = useSnackbar()

  const goBack = useGoBack()

  const messages = useMessages()

  const companies = makeCompanies(user)

  const createNewUserMutation = useCreateCompanyUser(companyId || '')

  const handleSubmit = (formValues: FormValues) =>
    createNewUserMutation
      .mutateAsync({ ...formValues })
      .then(() => {
        snackbar.success(t('snackbars.success.addUserMsg'))
        goBack()
      })
      .catch(() => {
        snackbar.error(t('snackBars.error.generalMsg'))
      })

  return (
    <FormProvider
      initialValues={{ companyId }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema(
        t('companyPage.addCompanyUsers.validations', { returnObjects: true }),
      )}
    >
      {!companyId && (
        <FormSelect label={messages.selectCompany} name="companyId">
          {companies.map((el) => (
            <Option key={el.companyId} value={el.companyId}>
              {el.name}
            </Option>
          ))}
        </FormSelect>
      )}
      <FormInput
        placeholder={messages.users?.name.placeholder}
        label={messages.users?.name.label}
        name="name"
      />
      <FormInput
        placeholder={messages.users?.surname.placeholder}
        label={messages.users.surname?.label}
        name="surname"
      />
      <FormInput
        placeholder={messages.users?.email.placeholder}
        label={messages.users.email?.label}
        name="email"
      />
      <FormPhone label={messages.users?.phone?.label} name="phone" />
      <RenderIsMDField />

      <PageActions>
        <Button onClick={goBack} variant="muted" shape="square">
          {messages.general.cancel}
        </Button>
        <Button type="submit" variant="primary" shape="square">
          {messages.general.save}
        </Button>
      </PageActions>
    </FormProvider>
  )
}

export const AddCompanyUserPage = () => {
  const messages = useMessages()

  return (
    <>
      <PageMainContent size="sm">
        <BackComponent />
        <PageTitle>
          <Typography variant="h4">{messages.pageTitle}</Typography>
        </PageTitle>
        <PageForm />
      </PageMainContent>
    </>
  )
}
